import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api-client.js';
import { formatCurrency, formatDate } from '@cardealeros/shared';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const PLATFORM_LABELS: Record<string, string> = {
  facebook_page: 'Facebook',
  facebook_marketplace: 'FB Marketplace',
  craigslist: 'Craigslist',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook_reels: 'Reels',
  offerup: 'OfferUp',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook_page: 'bg-blue-900/50 text-blue-400 border-blue-800',
  facebook_marketplace: 'bg-blue-900/50 text-blue-400 border-blue-800',
  craigslist: 'bg-orange-900/50 text-orange-400 border-orange-800',
  tiktok: 'bg-pink-900/50 text-pink-400 border-pink-800',
  youtube: 'bg-red-900/50 text-red-400 border-red-800',
  instagram: 'bg-purple-900/50 text-purple-400 border-purple-800',
  facebook_reels: 'bg-blue-900/50 text-blue-400 border-blue-800',
  offerup: 'bg-green-900/50 text-green-400 border-green-800',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-900/50 text-green-400',
  posted: 'bg-green-900/50 text-green-400',
  pending: 'bg-yellow-900/50 text-yellow-400',
  processing: 'bg-yellow-900/50 text-yellow-400',
  draft: 'bg-surface-700 text-surface-400',
  failed: 'bg-red-900/50 text-red-400',
  removed: 'bg-surface-700 text-surface-500',
  sold: 'bg-blue-900/50 text-blue-400',
  expired: 'bg-surface-700 text-surface-500',
  renewing: 'bg-yellow-900/50 text-yellow-400',
};

const STATUS_ICONS: Record<string, string> = {
  active: '\u2713',
  posted: '\u2713',
  pending: '\u23F3',
  processing: '\u23F3',
  failed: '\u2717',
  removed: '\u2212',
  expired: '\u23F0',
  sold: '\uD83D\uDCB0',
  draft: '\u270F',
  renewing: '\u21BB',
};

export default function MarketplacePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Filters ──
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showPlatformSettings, setShowPlatformSettings] = useState(false);

  // ── Data ──
  const { data: statsRes } = useQuery({
    queryKey: ['marketplace-stats'],
    queryFn: () => api.get<any>('/marketplace/stats'),
  });

  const { data: listingsRes, isLoading } = useQuery({
    queryKey: ['marketplace-listings', filterPlatform, filterStatus],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (filterPlatform !== 'all') params.set('platform', filterPlatform);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      return api.get<any>(`/marketplace/listings?${params.toString()}`);
    },
  });

  const { data: queueRes } = useQuery({
    queryKey: ['marketplace-queue'],
    queryFn: () => api.get<any>('/marketplace/queue'),
    refetchInterval: 10000,
  });

  const { data: platformsRes } = useQuery({
    queryKey: ['marketplace-platforms'],
    queryFn: () => api.get<any>('/marketplace/platforms'),
  });

  const removeMutation = useMutation({
    mutationFn: (listingId: string) => api.delete(`/marketplace/listings/${listingId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-listings'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-stats'] });
    },
  });

  const renewMutation = useMutation({
    mutationFn: (listingId: string) => api.post(`/marketplace/listings/${listingId}/renew`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-listings'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-stats'] });
    },
  });

  const toggleAutoPost = useMutation({
    mutationFn: ({ platform, autoPost }: { platform: string; autoPost: boolean }) =>
      api.patch(`/marketplace/platforms/${platform}`, { auto_post: autoPost }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketplace-platforms'] }),
  });

  const disconnectMutation = useMutation({
    mutationFn: (platform: string) => api.delete(`/marketplace/platforms/${platform}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-platforms'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-stats'] });
    },
  });

  const fbAuthMutation = useMutation({
    mutationFn: () => api.get<any>('/marketplace/facebook/auth-url'),
    onSuccess: (data) => {
      if (data.data?.auth_url) window.open(data.data.auth_url, '_blank');
    },
  });

  // ── Derived ──
  const stats = statsRes?.data;
  const listings = listingsRes?.data ?? [];
  const platforms = platformsRes?.data ?? [];
  const queueItems = (queueRes?.data ?? []).filter(
    (q: any) => q.status === 'pending' || q.status === 'processing'
  );

  // Group listings by vehicle for the grouped view
  const groupedByVehicle = useMemo(() => {
    const groups: Record<string, { vehicle: string; vehicleId: string; photo?: string; listings: any[] }> = {};
    for (const l of listings) {
      const key = l.vehicle_id;
      if (!groups[key]) {
        groups[key] = {
          vehicle: [l.year, l.make, l.model].filter(Boolean).join(' ') || l.vin || 'Unknown',
          vehicleId: l.vehicle_id,
          photo: l.photo_url,
          listings: [],
        };
      }
      groups[key].listings.push(l);
    }
    return Object.values(groups).sort((a, b) => {
      const aTime = Math.max(...a.listings.map((l: any) => new Date(l.created_at).getTime()));
      const bTime = Math.max(...b.listings.map((l: any) => new Date(l.created_at).getTime()));
      return bTime - aTime;
    });
  }, [listings]);

  // Active platforms for filter chips
  const activePlatforms = useMemo(() => {
    const set = new Set<string>();
    for (const l of listings) set.add(l.platform);
    return Array.from(set);
  }, [listings]);

  // Stat counts
  const activeCount = stats?.listings_by_status?.find((s: any) => s.status === 'active')?.count ?? 0;
  const pendingCount = (stats?.queue_pending ?? 0) + (stats?.listings_by_status?.find((s: any) => s.status === 'pending')?.count ?? 0);
  const failedCount = stats?.listings_by_status?.find((s: any) => s.status === 'failed')?.count ?? 0;
  const totalCount = stats?.listings_by_status?.reduce((sum: number, s: any) => sum + s.count, 0) ?? 0;

  return (
    <div className="page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold">Postings</h2>
          <p className="text-surface-500 text-xs mt-0.5">All your marketplace &amp; social listings in one place</p>
        </div>
        <button
          onClick={() => setShowPlatformSettings(!showPlatformSettings)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-800 text-surface-400 hover:text-white border border-surface-700 transition-colors"
        >
          {showPlatformSettings ? 'Hide' : 'Platforms'} ({platforms.length})
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <button onClick={() => { setFilterStatus('all'); setFilterPlatform('all'); }}
          className={`rounded-xl p-2.5 text-center transition-colors border ${filterStatus === 'all' && filterPlatform === 'all' ? 'border-brand-500 bg-brand-900/20' : 'border-surface-700 bg-surface-800/50'}`}>
          <p className="text-lg font-bold">{totalCount}</p>
          <p className="text-[10px] text-surface-500">Total</p>
        </button>
        <button onClick={() => { setFilterStatus('active'); setFilterPlatform('all'); }}
          className={`rounded-xl p-2.5 text-center transition-colors border ${filterStatus === 'active' ? 'border-green-700 bg-green-900/20' : 'border-surface-700 bg-surface-800/50'}`}>
          <p className="text-lg font-bold text-green-400">{activeCount}</p>
          <p className="text-[10px] text-surface-500">Active</p>
        </button>
        <button onClick={() => { setFilterStatus('pending'); setFilterPlatform('all'); }}
          className={`rounded-xl p-2.5 text-center transition-colors border ${filterStatus === 'pending' ? 'border-yellow-700 bg-yellow-900/20' : 'border-surface-700 bg-surface-800/50'}`}>
          <p className="text-lg font-bold text-yellow-400">{pendingCount}</p>
          <p className="text-[10px] text-surface-500">Pending</p>
        </button>
        <button onClick={() => { setFilterStatus('failed'); setFilterPlatform('all'); }}
          className={`rounded-xl p-2.5 text-center transition-colors border ${filterStatus === 'failed' ? 'border-red-700 bg-red-900/20' : 'border-surface-700 bg-surface-800/50'}`}>
          <p className="text-lg font-bold text-red-400">{failedCount}</p>
          <p className="text-[10px] text-surface-500">Failed</p>
        </button>
      </div>

      {/* Platform filter chips */}
      {activePlatforms.length > 1 && (
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            onClick={() => setFilterPlatform('all')}
            className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
              filterPlatform === 'all' ? 'bg-brand-600 text-white border-brand-500' : 'bg-surface-800 text-surface-400 border-surface-700'
            }`}
          >
            All
          </button>
          {activePlatforms.map(p => (
            <button
              key={p}
              onClick={() => setFilterPlatform(filterPlatform === p ? 'all' : p)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                filterPlatform === p
                  ? (PLATFORM_COLORS[p] ?? 'bg-surface-700 text-surface-400 border-surface-600')
                  : 'bg-surface-800 text-surface-400 border-surface-700'
              }`}
            >
              {PLATFORM_LABELS[p] ?? p}
            </button>
          ))}
        </div>
      )}

      {/* Platform settings panel (collapsible) */}
      {showPlatformSettings && (
        <div className="rounded-xl border border-surface-700 bg-surface-800/50 p-4 mb-4">
          <p className="text-xs text-surface-500 uppercase tracking-wider mb-3">Connected Platforms</p>
          {platforms.length > 0 ? (
            <div className="space-y-2 mb-4">
              {platforms.map((p: any) => (
                <div key={p.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${PLATFORM_COLORS[p.platform] ?? 'bg-surface-700 text-surface-400'}`}>
                      {PLATFORM_LABELS[p.platform] ?? p.platform}
                    </span>
                    <span className="text-sm text-surface-300">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-surface-500">Auto-post</span>
                    <button
                      onClick={() => toggleAutoPost.mutate({ platform: p.platform, autoPost: !p.auto_post })}
                      className={`w-9 h-5 rounded-full transition-colors relative ${p.auto_post ? 'bg-green-600' : 'bg-surface-600'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${p.auto_post ? 'left-[18px]' : 'left-0.5'}`} />
                    </button>
                    <button onClick={() => disconnectMutation.mutate(p.platform)} className="text-red-400 text-xs hover:text-red-300">&times;</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-surface-500 text-sm mb-3">No platforms connected yet.</p>
          )}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'facebook_page', label: 'Facebook', ready: true },
              { key: 'facebook_marketplace', label: 'FB Marketplace', ready: true },
              { key: 'instagram', label: 'Instagram', ready: true },
              { key: 'craigslist', label: 'Craigslist', ready: true },
              { key: 'tiktok', label: 'TikTok', ready: false },
            ].filter(p => !platforms.some((cp: any) => cp.platform === p.key)).map(p => (
              <button
                key={p.key}
                onClick={() => {
                  if (p.ready && (p.key.includes('facebook') || p.key === 'instagram')) fbAuthMutation.mutate();
                  if (p.key === 'craigslist') navigate('/connect');
                }}
                disabled={!p.ready}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  p.ready ? 'bg-surface-700 text-surface-300 border-surface-600 hover:border-brand-500' : 'bg-surface-800 text-surface-600 border-surface-700 cursor-not-allowed'
                }`}
              >
                + {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* In-progress queue */}
      {queueItems.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-yellow-400 font-medium">Processing {queueItems.length} item{queueItems.length !== 1 ? 's' : ''}...</p>
          </div>
          <div className="space-y-1.5">
            {queueItems.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-yellow-900/10 border border-yellow-900/30">
                <span className="text-sm text-surface-300 truncate">
                  {[item.year, item.make, item.model].filter(Boolean).join(' ')}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${PLATFORM_COLORS[item.platform] ?? ''}`}>
                  {PLATFORM_LABELS[item.platform] ?? item.platform}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main listings — grouped by vehicle */}
      {isLoading ? (
        <div className="text-center py-12 text-surface-500">Loading...</div>
      ) : groupedByVehicle.length > 0 ? (
        <div className="space-y-3">
          {groupedByVehicle.map((group) => (
            <div key={group.vehicleId} className="rounded-xl border border-surface-700 overflow-hidden">
              {/* Vehicle header */}
              <button
                onClick={() => navigate(`/vehicles/${group.vehicleId}`)}
                className="w-full flex items-center gap-3 p-3 bg-surface-800/50 hover:bg-surface-800 transition-colors text-left"
              >
                {group.photo && (
                  <img src={group.photo} alt="" className="w-12 h-9 object-cover rounded-lg flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{group.vehicle}</p>
                  <p className="text-[10px] text-surface-500">{group.listings.length} listing{group.listings.length !== 1 ? 's' : ''}</p>
                </div>
                <span className="text-surface-600 text-xs">&rsaquo;</span>
              </button>

              {/* Platform listings for this vehicle */}
              <div className="divide-y divide-surface-800">
                {group.listings.map((l: any) => (
                  <div key={l.id} className="px-3 py-2.5 flex items-center gap-2">
                    {/* Platform badge */}
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${PLATFORM_COLORS[l.platform] ?? 'bg-surface-700 text-surface-400'}`}>
                      {PLATFORM_LABELS[l.platform] ?? l.platform}
                    </span>

                    {/* Status */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${STATUS_COLORS[l.status] ?? 'bg-surface-700 text-surface-400'}`}>
                      {STATUS_ICONS[l.status] ?? ''} {l.status}
                    </span>

                    {/* Price + date */}
                    <div className="flex-1 min-w-0 text-right">
                      {l.price_cents ? (
                        <span className="text-xs text-surface-400">{formatCurrency(l.price_cents)}</span>
                      ) : null}
                      {l.posted_at && (
                        <span className="text-[10px] text-surface-600 ml-2">{formatDate(l.posted_at)}</span>
                      )}
                    </div>

                    {/* Error */}
                    {l.error_message && (
                      <span className="text-[10px] text-red-400 truncate max-w-[100px]" title={l.error_message}>
                        {l.error_message}
                      </span>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {l.external_url && (
                        <a
                          href={l.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-1.5 py-0.5 rounded text-[10px] bg-brand-900/30 text-brand-400 hover:bg-brand-900/50 transition-colors"
                          title="View on platform"
                        >
                          View
                        </a>
                      )}
                      {(l.status === 'active' || l.status === 'posted') && (
                        <button
                          onClick={() => removeMutation.mutate(l.id)}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-red-900/30 text-red-400 hover:bg-red-900/50 transition-colors"
                          title="Remove listing"
                        >
                          Remove
                        </button>
                      )}
                      {(l.status === 'expired' || l.status === 'removed') && (
                        <button
                          onClick={() => renewMutation.mutate(l.id)}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-green-900/30 text-green-400 hover:bg-green-900/50 transition-colors"
                          title="Renew listing"
                        >
                          Renew
                        </button>
                      )}
                      {l.status === 'failed' && (
                        <button
                          onClick={() => renewMutation.mutate(l.id)}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50 transition-colors"
                          title="Retry posting"
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-surface-400 text-lg mb-2">No postings yet</p>
          <p className="text-surface-600 text-sm mb-6">
            {platforms.length === 0
              ? 'Connect a platform to start posting your vehicles.'
              : 'Go to any vehicle and tap Post, FB, or CL to list it.'}
          </p>
          <button
            onClick={() => navigate(platforms.length === 0 ? '/connect' : '/dashboard')}
            className="btn-primary px-6"
          >
            {platforms.length === 0 ? 'Connect Platforms' : 'View Vehicles'}
          </button>
        </div>
      )}
    </div>
  );
}
